import { useEffect, useState } from "react";
import client from "../lib/api";
import { ShieldAlert, Lock, X } from "lucide-react";

/*
 * AgeGateModal — YouTube-style birth-year gate for R/Explicit content.
 *
 * Flow:
 *   1. Watch.jsx renders a video whose response has `age_gate_required: true`.
 *   2. This modal shows. User enters birth year.
 *   3. POST /api/age-gate/verify sets an HttpOnly cookie for 30 days on
 *      success. Modal closes and Watch.jsx re-fetches the video to get
 *      the real `playback_url`.
 *   4. On failure (age < 18) shows the denial message and offers "Take me
 *      back to safe content" which navigates home.
 */

export default function AgeGateModal({ open, onVerified, onCancel, videoTitle, minAge = 18, rating }) {
  const [year, setYear] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | denied | error
  const [detail, setDetail] = useState("");

  useEffect(() => { if (!open) { setYear(""); setState("idle"); setDetail(""); } }, [open]);

  if (!open) return null;

  async function submit() {
    const y = parseInt(year, 10);
    if (!y || y < 1900 || y > new Date().getFullYear()) {
      setState("error"); setDetail("Enter a valid 4-digit year."); return;
    }
    setState("submitting");
    try {
      const r = await client.post("/age-gate/verify", { birth_year: y, min_age: minAge });
      if (r.data?.verified) {
        onVerified?.();
      } else {
        setState("denied");
        setDetail(r.data?.detail || `Must be ${minAge} or older.`);
      }
    } catch (e) {
      setState("error");
      setDetail(e?.response?.data?.detail || e?.message || "Something went wrong.");
    }
  }

  // Copy varies by rating so the modal doesn't say "adult content" for PG-13
  const isAdult = minAge >= 18;
  const ratingLabel = rating || (isAdult ? "Adult" : `${minAge}+`);
  const headline = isAdult
    ? "This drop is for adults only"
    : rating === "PG-13"
      ? "Confirm you're 13 or older"
      : `Confirm you're ${minAge} or older`;
  const bodyCopy = isAdult
    ? `This video is rated <b>${ratingLabel}</b>. To watch, confirm your birth year.`
    : rating === "PG-13"
      ? `This video is rated <b>PG-13</b> — some scenes may not be suitable for younger viewers. Confirm your birth year to continue.`
      : `This video is rated <b>${ratingLabel}</b>. Confirm your birth year to continue.`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="age-gate-modal"
      role="dialog"
      aria-labelledby="age-gate-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-gradient-to-b from-[#151515] to-black border border-[#FF6B00]/30 shadow-2xl shadow-[#FF6B00]/20 p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-[#FF6B00]/15 border border-[#FF6B00]/40 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-[#FF6B00]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Content Notice · {ratingLabel}</p>
            <h2 id="age-gate-title" className="text-white text-xl font-black leading-tight mt-0.5">
              {headline}
            </h2>
            {videoTitle && (
              <p className="text-xs text-zinc-500 mt-1 truncate">{videoTitle}</p>
            )}
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-zinc-500 hover:text-white p-1 -m-1"
              data-testid="age-gate-close"
              aria-label="Close"
            ><X className="w-4 h-4" /></button>
          )}
        </div>

        {state !== "denied" ? (
          <>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: bodyCopy + " We&rsquo;ll remember your answer on this device for 30 days." }} />
            <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">
              Birth Year
            </label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="1900"
              max={new Date().getFullYear()}
              placeholder="YYYY"
              value={year}
              onChange={(e) => setYear(e.target.value.slice(0, 4))}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              data-testid="age-gate-year"
              autoFocus
              className="w-full h-14 rounded-lg bg-black/60 border border-white/10 focus:border-[#FF6B00] focus:outline-none px-4 text-2xl font-mono text-white text-center tracking-widest"
            />
            {state === "error" && (
              <p className="mt-2 text-xs text-red-400" data-testid="age-gate-error">{detail}</p>
            )}
            <button
              onClick={submit}
              disabled={state === "submitting" || !year}
              data-testid="age-gate-submit"
              className="mt-5 w-full h-12 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FFB800] text-black font-black uppercase tracking-widest text-sm hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:hover:scale-100 inline-flex items-center justify-center gap-2"
            >
              <Lock className="w-4 h-4" />
              {state === "submitting" ? "Verifying..." : "Confirm & Watch"}
            </button>
            <p className="mt-3 text-[10px] text-zinc-600 text-center">
              {isAdult
                ? "By confirming, you acknowledge you are 18+ and consent to viewing adult content."
                : `By confirming, you acknowledge you meet the ${ratingLabel} minimum age.`}
            </p>
          </>
        ) : (
          <div data-testid="age-gate-denied">
            <p className="text-sm text-red-300 leading-relaxed mb-4">
              <ShieldAlert className="w-4 h-4 inline mr-1" />
              {detail || `You must be ${minAge} or older to watch this.`}
            </p>
            <p className="text-sm text-zinc-400 mb-5">
              Plenty of safe drops in the Nexus. Let&apos;s get you there.
            </p>
            <button
              onClick={() => { window.location.href = "/"; }}
              data-testid="age-gate-back-home"
              className="w-full h-12 rounded-full bg-[#FFB800] text-black font-black uppercase tracking-widest text-sm"
            >
              Take me to safe content
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
